import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADVANCED_IDS, ADVANCED_TABS, TAB_PREFS_EVENT, allPinned, arrivalNote, autoReasons, buildTabRow, isTabHidden, markArrived, readArrived,
  readTabPrefs, saveTabPrefs, shownAdvanced, tabCounts, visibleAdvanced, withAllPinned, withMode, withPinToggled,
} from '../utils/projectTabsModel';
import { loadTabPrefs } from '../utils/tabPrefsSync';
import config from '../config';
import usePlanFeatures from './usePlanFeatures';
import { useHiddenWithTerminal } from './useTerminalTab';

/**
 * The person's project-tab preferences (utils/projectTabsModel.js), kept in sync across components and browser tabs:
 * `[prefs, setPrefs]`, `setPrefs(next | (prev) => next)`. Read from this browser at once, then from the person's
 * settings on the server (utils/tabPrefsSync.js, once per page), where every change goes too.
 */
export function useTabPrefs() {
  const [prefs, setState] = useState(readTabPrefs);
  useEffect(() => {
    loadTabPrefs();
    const sync = () => setState(readTabPrefs());
    const onStorage = (event) => { if (!event.key || event.key.startsWith('flow.projectTabs')) { sync(); } };
    window.addEventListener(TAB_PREFS_EVENT, sync);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(TAB_PREFS_EVENT, sync);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  const setPrefs = useCallback((next) => {
    const value = typeof next === 'function' ? next(readTabPrefs()) : next;
    setState(saveTabPrefs(value));
  }, []);
  return [prefs, setPrefs];
}

/**
 * One project's tab row: what is on it (`row`), what More offers (`more`), the temporary tab and the one-time note of
 * an auto tab that just arrived, with their actions. `view` is the open workspace view; an advanced one that is not on
 * the row becomes the temporary tab (opened from More, a link or the AI), until it is closed or pinned, or the project
 * changes. `signals`: what the page knows (projectTabsModel tabSignalsOf, plus `qualityBlocked` from useQualityGate).
 * The tabs the person's plan hides (usePlanFeatures `hiddenTabs`), and Terminal where it is not offered to this person
 * (hooks/useTerminalTab.js), are on none of them; `hidden` says which.
 */
export default function useProjectTabs({ projectId = null, view = null, page = null, signals = null } = {}) {
  const [prefs, setPrefs] = useTabPrefs();
  const { hiddenTabs } = usePlanFeatures();
  const withTerminal = useHiddenWithTerminal(hiddenTabs, projectId);
  // Logs is the sandbox's (each service's build and run output): without sandbox-first there is none to show.
  const hidden = config.sandboxFirst || withTerminal.includes('logs') ? withTerminal : [...withTerminal, 'logs'];
  const [temp, setTemp] = useState(null);
  const [arrived, setArrived] = useState(readArrived);
  // The server's values arrive (or another tab row changed them): the notes already seen too.
  useEffect(() => {
    const sync = () => setArrived(readArrived());
    window.addEventListener(TAB_PREFS_EVENT, sync);
    return () => window.removeEventListener(TAB_PREFS_EVENT, sync);
  }, []);
  const known = signals || {};
  const shown = shownAdvanced(prefs, known, hidden);
  const counts = tabCounts(known);
  const shownKey = `${ADVANCED_IDS.map((id) => shown[id] || '-').join(',')}|${counts.quality}|${hidden.join(',')}`;

  // Leaving the project closes the temporary tab.
  useEffect(() => { setTemp(null); }, [projectId]);
  // An advanced view that is not on the row: opened for now.
  // `page`: the project page on, 'code' (Home's Files or a service's editor) or 'logs' (Home's Logs view): Code and Logs
  // are advanced tabs, not workspace views.
  const viewTab = view?.tab || (page === 'code' || page === 'logs' ? page : null);
  useEffect(() => {
    if (viewTab && ADVANCED_IDS.includes(viewTab) && !shown[viewTab] && !isTabHidden(hidden, viewTab)) { setTemp(viewTab); }
  }, [viewTab, shownKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const reasons = autoReasons(known);
  const noteId = ADVANCED_IDS.find((id) => shown[id] === 'auto' && !arrived.includes(id)) || null;
  // Opening the tab that just arrived is as good as "Got it".
  useEffect(() => {
    if (noteId && viewTab === noteId) { setArrived(markArrived(noteId)); }
  }, [noteId, viewTab]);

  const row = useMemo(() => buildTabRow({ prefs, signals: known, temp, arrived, hidden }), [prefs, shownKey, temp, arrived]); // eslint-disable-line react-hooks/exhaustive-deps
  const tempOn = row.build.some((tab) => tab.kind === 'temp') ? temp : null;

  const more = visibleAdvanced(hidden).map((tab) => ({
    ...tab,
    mode: prefs.modes[tab.id],
    shown: shown[tab.id],
    reason: shown[tab.id] === 'auto' ? reasons[tab.id] : null,
    pinned: prefs.modes[tab.id] === 'pinned',
    count: counts[tab.id] || 0,
  }));

  const acknowledge = (id) => setArrived(markArrived(id));
  return {
    prefs,
    setPrefs,
    row,
    more,
    temp: tempOn,
    shown,
    hidden,
    allPinned: allPinned(prefs, hidden),
    note: noteId ? { id: noteId, label: ADVANCED_TABS.find((tab) => tab.id === noteId).label, ...arrivalNote(noteId, known) } : null,
    acknowledge,
    hide: (id) => {
      acknowledge(id);
      setPrefs((prev) => withMode(prev, id, 'more'));
    },
    togglePin: (id) => {
      setPrefs((prev) => withPinToggled(prev, id));
      if (temp === id) { setTemp(null); }
    },
    pinTemp: () => {
      if (!tempOn) { return; }
      setPrefs((prev) => withMode(prev, tempOn, 'pinned'));
      setTemp(null);
    },
    closeTemp: () => setTemp(null),
    setAllPinned: (on) => setPrefs((prev) => withAllPinned(prev, on, hidden)),
  };
}
